import { FC, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../Components/ui/table";
import { useSelector } from "react-redux";
import { RootState } from "../../../State/store";
import axios from "axios";
import { useToast } from "../../../Components/ui/use-toast";
import { Toaster } from "../../../Components/ui/toaster";
import { CaretSortIcon } from "@radix-ui/react-icons";
import CircularProgress from "@mui/material/CircularProgress";
import { Badge } from "../../../Components/ui/badge";
import { useLocation } from "react-router-dom";

export interface ChannelConfig {
  channel_config_id: number;
  channel_id: number;
  channel_config_name: string;
  channel_status: string;
  partner_type: string;
  partner_id: number;
  channelserver_url: string;
  channelserver_app_status: string;
  channelserver_app_remarks: string;
  created_time: string;
  last_updated_time: string;
  channel_config: string;
  api_type: string;
}

const AdminWhatsapp: FC = () => {
  const [tableData, setTableData] = useState<ChannelConfig[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const toast = useToast();
  const location = useLocation();

  const adminUrl = useSelector(
    (state: RootState) => state.authentication.adminUrl
  );

  const channelId = location.state?.channelId || 1;

  // Pagination calculation
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}/${mm}/${dd}`;
  };

  const fetchChannelConfigs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${adminUrl}/GetChannelConfigsByChannelId`,
        {
          params: {
            channelId,
            page: currentPage,
            pageSize: rowsPerPage,
          },
        }
      );
      if (
        response.data.status === "Success" &&
        Array.isArray(response.data.channelConfigs) &&
        response.data.channelConfigs.length > 0
      ) {
        setTableData(response.data.channelConfigs);
        setTotalCount(response.data.total);
      } else {
        setTableData([]);
        setTotalCount(0);
      }
    } catch (err) {
      setTableData([]);
      setTotalCount(0);
      toast.toast({
        title: "Error",
        description: "Failed to fetch channel configurations",
      });
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch whenever page or rowsPerPage changes
  useEffect(() => {
    fetchChannelConfigs();
    // eslint-disable-next-line
  }, [currentPage, rowsPerPage]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px]">
        <CircularProgress color="primary" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-[1rem]">
        <div className="rounded-md border overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            <Table
              className="rounded-xl border-gray-200 min-w-full"
              style={{ color: "#020202", fontSize: "15px" }}
            >
              <TableHeader className="text-center text-[14px] font-medium">
                <TableRow className="sticky top-0 bg-white z-10">
                  <TableHead className="text-left min-w-[180px]">
                    <div className="flex items-center gap-2 justify-start pl-4">
                      Configuration Name
                    </div>
                  </TableHead>
                  <TableHead className="text-left min-w-[100px]">
                    <div className="flex items-center gap-2 justify-start">
                      Type
                    </div>
                  </TableHead>
                  <TableHead className="text-left min-w-[100px]">
                    <div className="flex items-center gap-2 justify-start">
                      Status
                    </div>
                  </TableHead>
                  <TableHead className="text-left min-w-[200px]">
                    <div className="flex items-center gap-2 justify-start">
                      Channel Server Connection URL
                    </div>
                  </TableHead>
                  <TableHead className="text-left min-w-[120px]">
                    <div className="flex items-center gap-2 justify-start">
                      Created Time
                    </div>
                  </TableHead>
                  <TableHead className="text-left min-w-[120px]">
                    <div className="flex items-center gap-2 justify-start">
                      Last Updated
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody className="text-left text-[14px] font-normal text-[#020617]">
                {tableData.length > 0 ? (
                  tableData.map((data) => (
                    <TableRow key={data.channel_config_id}>
                      <TableCell className="py-4 text-left pl-6">
                        <div
                          className="truncate max-w-[160px]"
                          title={data.channel_config_name || "-"}
                        >
                          {data.channel_config_name || "-"}
                        </div>
                      </TableCell>
                      <TableCell className="py-4 text-left">
                        {data.api_type || "-"}
                      </TableCell>
                      <TableCell className="py-4 text-left">
                        <Badge
                          variant={
                            data.channel_status === "active"
                              ? "default"
                              : "secondary"
                          }
                        >
                          {data.channel_status || "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4 text-left">
                        <div
                          className="truncate max-w-[180px]"
                          title={data.channelserver_url || "Not configured"}
                        >
                          {data.channelserver_url || "Not configured"}
                        </div>
                      </TableCell>
                      <TableCell className="py-4 text-left whitespace-nowrap">
                        {formatDate(data.created_time)}
                      </TableCell>
                      <TableCell className="py-4 text-left whitespace-nowrap">
                        {formatDate(data.last_updated_time)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-4 text-center text-gray-500"
                    >
                      No WhatsApp channel configurations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Pagination Section */}
        <div className="flex justify-between items-center mt-4 px-4 pb-4">
          <div className="flex items-center space-x-2 text-gray-500 text-sm">
            <span>
              {totalCount === 0
                ? "0 of 0 row(s) selected"
                : `${(currentPage - 1) * rowsPerPage + 1}-${Math.min(
                    currentPage * rowsPerPage,
                    totalCount
                  )} of ${totalCount} row(s) selected`}
            </span>
          </div>

          <div className="flex items-center space-x-4 font-medium text-sm">
            <span className="text-[#020617] font-medium text-[14px]">
              Rows per page
            </span>
            <div className="relative inline-block ml-2">
              <select
                disabled={totalCount === 0}
                className={`cursor-pointer border border-gray-300 rounded-md px-2 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  totalCount === 0 ? "cursor-not-allowed bg-gray-100" : ""
                }`}
                style={{
                  width: "60px",
                  height: "30px",
                  textAlign: "left",
                  fontSize: "12px",
                  fontWeight: "400",
                  borderColor: "#E5E7EB",
                  boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[5, 10, 20].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              <CaretSortIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4" />
            </div>

            <div className="ml-4 mr-4">
              <span className="text-[#020617] text-[14px] font-medium">{`Page ${currentPage} of ${totalPages || 1}`}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={currentPage === 1 || totalCount === 0}
                className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                  currentPage === 1 || totalCount === 0
                    ? "cursor-not-allowed bg-gray-100"
                    : "hover:bg-gray-200"
                }`}
                onClick={() => setCurrentPage(1)}
              >
                «
              </button>
              <button
                disabled={currentPage === 1 || totalCount === 0}
                className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                  currentPage === 1 || totalCount === 0
                    ? "cursor-not-allowed bg-gray-100"
                    : "hover:bg-gray-200"
                }`}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                ‹
              </button>
              <button
                disabled={currentPage === totalPages || totalCount === 0}
                className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                  currentPage === totalPages || totalCount === 0
                    ? "cursor-not-allowed bg-gray-100"
                    : "hover:bg-gray-200"
                }`}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                ›
              </button>
              <button
                disabled={currentPage === totalPages || totalCount === 0}
                className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                  currentPage === totalPages || totalCount === 0
                    ? "cursor-not-allowed bg-gray-100"
                    : "hover:bg-gray-200"
                }`}
                onClick={() => setCurrentPage(totalPages)}
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>
      <Toaster />
    </>
  );
};

export default AdminWhatsapp;